// Pure style/data helpers for the PML Snapshot hex map — no React, no mapbox
// imports, fully unit-testable. Task 13 (HexMap) turns these into mapbox-gl
// layer paint properties; ConcernRegister / CellDetailDrawer reuse the same
// METRICS/formatUsd/legendTicks so labels stay in sync across the page.

export const SCALES = [5, 10, 25, 50];

// metric key -> how to read + paint it. 'quant' = sequential YlOrRd, 'diverging' = red/green
// around 0, 'status' = categorical green/amber/red.
export const METRICS = {
  pml: { prop: 'pml250Agg', label: '1-in-250 PML', kind: 'quant', format: 'usd' },
  aal: { prop: 'aal', label: 'AAL', kind: 'quant', format: 'usd' },
  tiv: { prop: 'tiv', label: 'TIV', kind: 'quant', format: 'usd' },
  locs: { prop: 'nLocations', label: 'Locations', kind: 'quant', format: 'int' },
  status: { prop: 'status', label: 'Status', kind: 'status' },
  deltaMom: { prop: 'deltaMom', label: 'MoM Δ PML', kind: 'diverging', format: 'usd' },
  deltaMce: { prop: 'deltaMce', label: 'MCE effect', kind: 'diverging', format: 'usd' },
};

export const STATUS_COLORS = { green: '#078B80', amber: '#F68A5E', red: '#F97473' };

// Fallback colors for features that don't (yet) have a status.
const STATUS_FILL_FALLBACK = '#CCCCCC';
const STATUS_LINE_FALLBACK = '#8A94A6';

const YL_OR_RD = ['#FFFFCC', '#FEB24C', '#FD8D3C', '#E31A1C', '#800026'];
const DIVERGING = ['#078B80', '#F4F6F8', '#B71C1C']; // green, neutral, red

const isNil = (v) => v === null || v === undefined;

// Guard against a degenerate (0 or negative) peak collapsing the interpolation
// domain into a single point — mapbox (and our own math below) requires
// strictly increasing stops.
const safePeak = (peak) => (Number(peak) > 0 ? Number(peak) : 1);

// Linear-interpolate between two '#rrggbb' colors at t in [0, 1].
const lerpColor = (a, b, t) => {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const chan = (shift) => {
    const ca = (pa >> shift) & 0xff;
    const cb = (pb >> shift) & 0xff;
    return Math.round(ca + (cb - ca) * t);
  };
  const toHex = (n) => n.toString(16).padStart(2, '0').toUpperCase();
  return `#${toHex(chan(16))}${toHex(chan(8))}${toHex(chan(0))}`;
};

// Sample a ramp of colors evenly spaced across [0, 1] at fraction t.
const sampleRamp = (colors, t) => {
  const clamped = Math.min(1, Math.max(0, t));
  const segments = colors.length - 1;
  const scaled = clamped * segments;
  const i = Math.min(segments - 1, Math.floor(scaled));
  return lerpColor(colors[i], colors[i + 1], scaled - i);
};

// 30776997 -> '$30.8M'; 800000 -> '$800k'; 412 -> '$412'; null/undefined -> '—'.
export function formatUsd(v) {
  if (isNil(v)) return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  let body;
  if (abs >= 1e6 || Math.round(abs / 1e3) >= 1000) {
    body = `$${(abs / 1e6).toFixed(1).replace(/\.0$/, '')}M`;
  } else if (abs >= 1e3) {
    body = `$${Math.round(abs / 1e3)}k`;
  } else {
    body = `$${Math.round(abs)}`;
  }
  return `${sign}${body}`;
}

const formatInt = (v) => (isNil(v) ? '—' : Math.round(Number(v)).toLocaleString('en-US'));

const formatByMetric = (metricKey, v) => (METRICS[metricKey]?.format === 'int' ? formatInt(v) : formatUsd(v));

// Max |value| across feature properties for the given prop; 0 if none present.
export function peakOf(features, prop) {
  let peak = 0;
  (features || []).forEach((f) => {
    const v = f?.properties?.[prop];
    if (isNil(v)) return;
    const abs = Math.abs(Number(v));
    if (!Number.isNaN(abs) && abs > peak) peak = abs;
  });
  return peak;
}

const statusMatchExpression = (fallback) => [
  'match',
  ['get', 'status'],
  'green',
  STATUS_COLORS.green,
  'amber',
  STATUS_COLORS.amber,
  'red',
  STATUS_COLORS.red,
  fallback,
];

// Mapbox data-driven expression for the hex fill layer's paint property.
export function fillExpression(metricKey, peak) {
  const metric = METRICS[metricKey];
  if (!metric) throw new Error(`Unknown metric: ${metricKey}`);

  if (metric.kind === 'status') return statusMatchExpression(STATUS_FILL_FALLBACK);

  const input = ['coalesce', ['get', metric.prop], 0];
  const p = safePeak(peak);

  if (metric.kind === 'diverging') {
    return ['interpolate', ['linear'], input, -p, DIVERGING[0], 0, DIVERGING[1], p, DIVERGING[2]];
  }

  // quant
  return ['interpolate', ['linear'], input, 0, YL_OR_RD[0], p * 0.25, YL_OR_RD[1], p * 0.5, YL_OR_RD[2], p * 0.75, YL_OR_RD[3], p, YL_OR_RD[4]];
}

// Mapbox data-driven expression for the hex outline layer's line-color.
export function statusLineExpression() {
  return statusMatchExpression(STATUS_LINE_FALLBACK);
}

// [{value, label, color}] spanning 0..peak (quant), -peak..peak (diverging),
// or the three status swatches (status) — 5 evenly spaced ticks for
// quant/diverging, midpoint 0 for diverging.
export function legendTicks(metricKey, peak) {
  const metric = METRICS[metricKey];
  if (!metric) throw new Error(`Unknown metric: ${metricKey}`);

  if (metric.kind === 'status') {
    return [
      { value: 'green', label: 'Green', color: STATUS_COLORS.green },
      { value: 'amber', label: 'Amber', color: STATUS_COLORS.amber },
      { value: 'red', label: 'Red', color: STATUS_COLORS.red },
    ];
  }

  const p = safePeak(peak);

  if (metric.kind === 'diverging') {
    return [0, 1, 2, 3, 4].map((i) => {
      const value = -p + (2 * p * i) / 4;
      const t = (value + p) / (2 * p);
      return { value, label: formatByMetric(metricKey, value), color: sampleRamp(DIVERGING, t) };
    });
  }

  // quant
  return [0, 1, 2, 3, 4].map((i) => {
    const value = (p * i) / 4;
    const t = value / p;
    return { value, label: formatByMetric(metricKey, value), color: sampleRamp(YL_OR_RD, t) };
  });
}

// deltaMom is hidden when no feature carries a value for it; every other
// metric is always enabled.
export function metricEnabled(metricKey, meta, features) {
  if (metricKey !== 'deltaMom') return true;
  return (features || []).some((f) => !isNil(f?.properties?.deltaMom));
}
